import { I18N } from '@/app'
import { APIClient } from '@/services'
import { analytics } from '@/services/analytics/segment'
import { Hurdle, HurdleCompare } from '@/types'
import { setFreeHurdlePlayed } from '@/utils'
import { useBooleanToggle } from '@codeleap/common'
import { useMutation } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { create, useStore } from 'zustand'

const currentGuess = create(() => '')

const errors = {
  'You have already tried this word': I18N.t('hurdle.alreadyGuessed'),
  'Invalid guess data': I18N.t('hurdle.error')
}

function useHandle(hurdle: Hurdle) {
  const [currentHurdle, setCurrentHurdle] = useState(hurdle)
  const [visible, toggle] = useBooleanToggle(false)
  const [showError, setShowError] = useState(false)
  const [dictError, setDictError] = useState<string | null>(null)

  const guessMutation = useMutation({
    mutationFn: APIClient.Games.hurdleManager.actions.guess,
    mutationKey: ['hurdle-guess']
  })

  const guesses = currentHurdle?.guesses

  const { turn, isCorrect, usedKeys } = useMemo(() => {
    const usedKeys = guesses
      ?.map(guess => guess?.comparison)
      .flat()
      .reduce(
        (acc, compare) => {
          acc[compare.character] = compare.outcome
          return acc
        },
        {} as { [key: string]: HurdleCompare['outcome'] }
      )

    const turn = guesses?.length
    const isCorrect = guesses?.some(guess => guess?.correct)

    return { turn, isCorrect, usedKeys }
  }, [guesses?.length])

  const totalTurns = hurdle?.max_attempts
  const hurdleLength = hurdle?.max_attempts - 1
  const gameEnded = turn >= totalTurns || isCorrect

  const checkCurrentGuess = useCallback(async () => {
    const guess = currentGuess.getState().toLowerCase()

    const result = (await guessMutation.mutateAsync({ guess, hurdleId: hurdle?.id })) as Hurdle & { error: string }

    if (result?.error) {
      setShowError(true)
      setDictError(errors[result.error])
      return setTimeout(() => setShowError(false), 200)
    }

    setCurrentHurdle(result)
    APIClient.Games.hurdleManager.updateItems(result)
    currentGuess.setState('')

    const ended = result.completed || result?.guesses?.length == result.max_attempts

    if (ended) {
      setFreeHurdlePlayed(true)

      setTimeout(() => toggle(), 2000)
    }
  }, [])

  const handleContinue = () => {
    toggle()

    if (isCorrect) {
      analytics.track('hurdle_correct', {
        tries: turn
      })
    } else {
      analytics.track('hurdle_incorrect')
    }
  }

  const handleKeyup = useCallback(
    (key: string) => {
      const guess = currentGuess.getState()

      if (gameEnded && key !== 'Enter') return

      if (dictError) {
        setTimeout(() => setDictError(null), 150)
      }

      if (key === 'Enter') {
        if (gameEnded) return handleContinue()

        if (guess.length !== hurdleLength) return

        checkCurrentGuess()
      } else if (key === 'Backspace' || key === '<') {
        currentGuess.setState(prev => prev.slice(0, -1))
      } else if (/^[A-Za-z]$/.test(key)) {
        if (guess.length < hurdleLength) {
          currentGuess.setState(prev => prev + key.toUpperCase())
        }
      }
    },
    [dictError]
  )

  return {
    handleKeyup,
    handleContinue,
    turn,
    currentGuess,
    usedKeys,
    hurdle: currentHurdle,
    isCorrect,
    gameEnded,
    totalTurns,
    toggle,
    dictError,
    visible,
    showError,
    hurdleLength,
    guesses,
    useCurrentGuess: () => useStore(currentGuess),
    loading: guessMutation.isPending
  }
}

const HurdleContext = createContext({} as ReturnType<typeof useHandle>)

export const HurdleProvider = ({ hurdle, children }) => {
  const handle = useHandle(hurdle)

  return <HurdleContext.Provider value={handle}>{children}</HurdleContext.Provider>
}

export const useHurdle = () => {
  return useContext(HurdleContext)
}
